/**
 * PreservedStateSkillEffect — Astral oracle-path upgrade: full Phase Step
 * cooldown reset on Temporal Echo revert.
 *
 * Code-only upgrade for temporal_echo. When this node unlocks, it registers
 * a CooldownHook that listens for the `temporal_echo_revert` condition
 * (fired by TemporalEchoSkillEffect on any manual or lethal revert) and
 * refunds 100% of Phase Step's cooldown. The 100ms global minimum floor
 * from CooldownHookSystem still applies.
 *
 * Pattern: identical to FoldedSpacePassiveSkillEffect — constructor-time
 * hook registration, no runtime tick logic.
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see astral-spec.md § temporal_echo_upgrade_2
 * @see ADR-064 (attunement shared systems architecture)
 */

import type { SkillEffect } from '../../SkillEffect.js';
import type { CooldownHook } from '../../../systems/CooldownHookSystem.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface PreservedStateDeps {
  /** CooldownHookSystem — hook is registered at unlock time (constructor). */
  readonly cooldownHooks: {
    register(hook: CooldownHook): void;
  };
}

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class PreservedStateSkillEffect implements SkillEffect {
  readonly skillId = 'temporal_echo_upgrade_2';
  readonly type = 'passive' as const;
  readonly activationMode = 'instant' as const;

  constructor(deps: PreservedStateDeps) {
    // Register cooldown hook at unlock time — full Phase Step refund on revert.
    deps.cooldownHooks.register({
      id: 'preserved_state_hook',
      trigger: { type: 'onCondition', conditionId: 'temporal_echo_revert' },
      targetSkillId: 'blink', // phase_step inherits blink's cooldown slot
      effect: { type: 'refundPercent', percent: 1.0 },
    });
  }

  // --- SkillEffect interface ---

  activate(): boolean { return false; }
  isActive(): boolean { return false; }
  update(_delta: number): void { /* passive — nothing to tick */ }
  destroy(): void { /* hook unregistration handled by SkillProgressionComponent */ }
}
